import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';

/**
 * A matrix with a secondaryDimensionA is fetched as
 * groupBy (A) -> subGroupBy (secondary A) -> trend (B), so each row nests its
 * secondary terms, each with its own date series:
 *
 *   { truck1, [m], values: [{ driverCode, [m], values: [{ date, [m] }] }] }
 *
 * This flattens them into one row per (A, secondary A) pair, in the same shape
 * as a plain A x date matrix, so the existing 2D date formatting applies:
 *
 *   { truck1, driverCode, [m], values: [{ date, [m] }] }
 *
 * Without columns (a column per metric) there's no trend, so each secondary
 * term carries its metrics itself, and its row is a plain 1D row:
 *
 *   { city, [m], values: [{ driver, [m] }] } -> { city, driver, [m] }
 *
 * Either way a pair row has only its own values, never its A's.
 *
 * Rows that aren't nested (e.g. special metrics, which ignore subGroupBy) are
 * returned unchanged.
 */
const flattenSecondaryDimensionRows = ({
  chartDef,
  response,
}: {
  chartDef: V5ChartDefinition;
  response: MetricsResponse;
}): MetricsResponse => {
  const { dimensionA, dimensionB } = chartDef;
  if (!dimensionA || !isSecondaryDimensionAInEffect(chartDef)) {
    return response;
  }

  return response.flatMap((row) => {
    const subRows = row.values as MetricResponse[] | undefined;
    const isNested =
      Array.isArray(subRows) &&
      (!dimensionB || subRows.some((s) => Array.isArray(s.values)));
    if (!isNested) {
      return [row];
    }

    return subRows.map((subRow) => ({
      [dimensionA.field]: row[dimensionA.field],
      filterInput: row.filterInput,
      dateScope: row.dateScope,
      ...subRow,
    }));
  });
};

export default flattenSecondaryDimensionRows;
